import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  DataTable, Table, TableHead, TableRow, TableHeader, TableBody, TableCell,
  TableContainer, TableToolbar, TableToolbarContent, TableToolbarSearch,
  Button, Modal, TextInput, TextArea, FormGroup, Pagination, Tag,
  OverflowMenu, OverflowMenuItem, InlineNotification, InlineLoading,
  Dropdown, ComboBox,
} from "@carbon/react";
import { Add, Filter } from "@carbon/icons-react"; // ✅ Added Filter icon
import { GoogleMap, Marker, Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import { callApi } from "../../services/apiService";

// ⚠️ Confirm these values with your backend dev
const VERIFY = { PENDING: "Pending", VERIFIED: "Verified", REJECTED: "Rejected" };

const VERIFICATION_OPTIONS = [
  { id: "", label: "Choose" },
  { id: "Pending", label: "Pending" },
  { id: "Verified", label: "Verified" },
  { id: "Rejected", label: "Rejected" },
];

const STATUS_OPTIONS = [
  { id: "", label: "Choose" },
  { id: "Active", label: "Active" },
  { id: "Inactive", label: "Inactive" },
];

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const MAP_LIBRARIES = ["places"];
const defaultCenter = { lat: 8.0883, lng: 77.5385 };
const mapContainerStyle = {
  width: "100%", height: "320px", borderRadius: "8px", border: "1px solid #e0e0e0",
};

// Same fixed values the property form uses (Kanyakumari)
const FIXED_LOCATION = { COUNTRY_ID: 1, STATE_ID: 1, DISTRICT_ID: 1 };

const EMPTY = {
  BUSINESS_NAME: "", ABOUT: "", WEBSITE: "", OFFICE_CONTACT_NUMBER: "",
  YEARS_OF_EXPERIENCE: "", ADDRESS_LINE_1: "", ADDRESS_LINE_2: "", LANDMARK: "",
  TALUK_ID: "", VILLAGE_ID: "", AREA_ID: "", PIN_CODE: "",
  LATITUDE: "", LONGITUDE: "",
  REGISTRATION_TYPE: "", REGISTRATION_NUMBER: "", RERA_NUMBER: "",
  GST_NUMBER: "", PAN_NUMBER: "", USER_ID: "",
};

const FIELDS = [
  ["BUSINESS_NAME", "Business Name *"],
  ["OFFICE_CONTACT_NUMBER", "Office Contact"], ["WEBSITE", "Website"],
  ["YEARS_OF_EXPERIENCE", "Years of Experience"], ["REGISTRATION_TYPE", "Registration Type"],
  ["REGISTRATION_NUMBER", "Registration Number"], ["RERA_NUMBER", "RERA Number"],
  ["GST_NUMBER", "GST Number"], ["PAN_NUMBER", "PAN Number"],
];

const NUMERIC_KEYS = ["YEARS_OF_EXPERIENCE"];
const DIGIT_ONLY_KEYS = ["OFFICE_CONTACT_NUMBER"];

const MAX_LEN = {
  BUSINESS_NAME: 255, OFFICE_CONTACT_NUMBER: 10, WEBSITE: 300,
  REGISTRATION_TYPE: 50, REGISTRATION_NUMBER: 100, RERA_NUMBER: 100,
  GST_NUMBER: 15, PAN_NUMBER: 10,
};

const PLACEHOLDERS = {
  OFFICE_CONTACT_NUMBER: "10-digit mobile number",
  WEBSITE: "https://www.example.com",
  YEARS_OF_EXPERIENCE: "e.g. 5",
  GST_NUMBER: "e.g. 39ABCDE8944F1Z8",
  REGISTRATION_NUMBER: "U45200KA2012PTC064321",
  PAN_NUMBER: "e.g. ABCDE1234F",
  RERA_NUMBER: "PRM/KA/RERA/...",
};

const MAX_IMAGE_MB = 5;
const MAX_PDF_MB = 10;

const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

// ---------- field validation (returns "" when valid) ----------
const validateField = (key, value) => {
  const v = String(value ?? "").trim();
  switch (key) {
    case "USER_ID":
      return v ? "" : "Please select a user for this builder profile.";
    case "BUSINESS_NAME":
      if (!v) return "Business name is required.";
      if (v.length < 2) return "Business name must be at least 2 characters.";
      if (v.length > 255) return "Business name must be 255 characters or fewer.";
      return "";
    case "OFFICE_CONTACT_NUMBER":
      if (v && !/^[6-9]\d{9}$/.test(v))
        return "Enter a valid 10-digit mobile number starting with 6, 7, 8 or 9.";
      return "";
    case "WEBSITE": {
      if (!v) return "";
      const msg = "Enter a valid website address starting with http:// or https://";
      try {
        const u = new URL(v);
        if (!["http:", "https:"].includes(u.protocol) || !u.hostname.includes(".")) return msg;
      } catch (e) {
        return msg;
      }
      return "";
    }
    case "YEARS_OF_EXPERIENCE":
      if (v && (!/^\d+$/.test(v) || Number(v) > 100))
        return "Enter a whole number between 0 and 100.";
      return "";
    case "REGISTRATION_NUMBER":
    case "RERA_NUMBER":
      if (v.length > 100) return "This value must be 100 characters or fewer.";
      return "";
    case "GST_NUMBER":
      if (v && !GST_REGEX.test(v))
        return "Enter a valid 15-character GST number, e.g. 39ABCDE8944F1Z8.";
      return "";
    case "PAN_NUMBER":
      if (v && !PAN_REGEX.test(v))
        return "Enter a valid PAN: 5 letters, 4 digits, 1 letter, e.g. ABCDE1234F.";
      return "";
    case "PIN_CODE":
      if (v && !/^\d{6}$/.test(v)) return "Pincode must be exactly 6 digits.";
      return "";
    default:
      return "";
  }
};

const VALIDATED_KEYS = [
  "USER_ID", "BUSINESS_NAME", "OFFICE_CONTACT_NUMBER", "WEBSITE", "YEARS_OF_EXPERIENCE",
  "REGISTRATION_NUMBER", "RERA_NUMBER", "GST_NUMBER", "PAN_NUMBER", "PIN_CODE",
];

const validateAll = (f) => {
  const out = {};
  VALIDATED_KEYS.forEach((k) => {
    const m = validateField(k, f[k]);
    if (m) out[k] = m;
  });
  return out;
};

const fieldDomId = (key) =>
  key === "USER_ID" ? "builder-user" : key === "PIN_CODE" ? "builder-pin" : `builder-${key}`;

const scrollToField = (key) =>
  setTimeout(() => {
    document.getElementById(fieldDomId(key))?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, 50);

const NO_FILE_ERRORS = { logo: "", banner: "", doc: "" };

const headers = [
  { key: "businessName", header: "Business Name" },
  { key: "contact", header: "Contact" },
  { key: "rera", header: "RERA No." },
  { key: "gst", header: "GST No." },
  { key: "verificationStatus", header: "Verification" },
  { key: "status", header: "Status" },
  { key: "createdAt", header: "Created At" },
  { key: "actions", header: "Actions" },
];

const norm = (v) => String(v || "").toUpperCase().trim();

const serializeBounds = (bounds) => {
  if (!bounds) return undefined;
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  return {
    northeast: { lat: ne.lat(), lng: ne.lng() },
    southwest: { lat: sw.lat(), lng: sw.lng() },
  };
};

const findOpt = (opts, id) => opts.find((o) => o.id === String(id)) || null;

export default function BuilderManagement() {
  const mountedRef = useRef(true);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [searchTerm, setSearchTerm] = useState("");

  // --- Filter States ---
  // draftFilters: bound to inputs (does NOT trigger API)
  const [draftFilters, setDraftFilters] = useState({
    verification_status: "",
    status: "",
    start_date: "",
    end_date: "",
  });
  
  // appliedFilters: used to trigger the API fetch
  const [appliedFilters, setAppliedFilters] = useState({
    verification_status: "",
    status: "",
    start_date: "",
    end_date: "",
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [fileErrors, setFileErrors] = useState(NO_FILE_ERRORS);
  const [logo, setLogo] = useState(null);
  const [banner, setBanner] = useState(null);
  const [existingLogo, setExistingLogo] = useState("");
  const [existingBanner, setExistingBanner] = useState("");
  const [removeLogo, setRemoveLogo] = useState(false);
  const [removeBanner, setRemoveBanner] = useState(false);
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const [docFile, setDocFile] = useState(null);
  const [existingDoc, setExistingDoc] = useState("");
  const [removeDoc, setRemoveDoc] = useState(false);
  const docInputRef = useRef(null);
  const docPreview = useMemo(() => (docFile ? URL.createObjectURL(docFile) : ""), [docFile]);
  useEffect(() => () => { if (docPreview) URL.revokeObjectURL(docPreview); }, [docPreview]);

  const logoPreview = useMemo(() => (logo ? URL.createObjectURL(logo) : ""), [logo]);
  const bannerPreview = useMemo(() => (banner ? URL.createObjectURL(banner) : ""), [banner]);

  useEffect(() => () => { if (logoPreview) URL.revokeObjectURL(logoPreview); }, [logoPreview]);
  useEffect(() => () => { if (bannerPreview) URL.revokeObjectURL(bannerPreview); }, [bannerPreview]);
  const [saving, setSaving] = useState(false);

  const [confirm, setConfirm] = useState(null); // { row, status }
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // file key → API file_type value
  const FILE_TYPE_BY_KEY = {
    logo: "logo",
    banner: "banner",
    doc: "registration-document",
  };

  // inside component:
  const [removingFile, setRemovingFile] = useState(""); // "logo" | "banner" | "doc" | ""

  // ---- location masters + map ----
  const [taluks, setTaluks] = useState([]);
  const [villages, setVillages] = useState([]);
  const [areas, setAreas] = useState([]);
  const [users, setUsers] = useState([]);

  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [searchAddress, setSearchAddress] = useState("");
  const [gmapError, setGmapError] = useState("");
  const [locating, setLocating] = useState(false);

  const autocompleteRef = useRef(null);
  const geocoderRef = useRef(null);

  const { isLoaded: mapsLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: MAP_LIBRARIES,
  });

  useEffect(() => {
    if (mapsLoaded && window.google && !geocoderRef.current) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
  }, [mapsLoaded]);

  useEffect(() => {
    const loadMasters = async () => {
      try {
        const t = await callApi("GET", "/taluk-masters/");
        setTaluks(t?.data ?? []);
        const v = await callApi("GET", "/villages/");
        setVillages(v?.data ?? []);
        const a = await callApi("GET", "/areas/");
        setAreas(a?.data ?? []);
      } catch (err) {
        console.error("Failed to load location masters", err);
        setApiError("Failed to load location master data");
      }
    };
    loadMasters();
  }, []);

  const fetchAvailableUsers = useCallback(async () => {
    try {
      let all = [];
      let p = 1;
      let total = 0;
      do {
        const res = await callApi("GET", "/user-profiles/available-users", { page: p });
        const items = res?.data?.items ?? [];
        total = res?.data?.total ?? items.length;
        all = [...all, ...items];
        if (items.length === 0) break;
        p += 1;
      } while (all.length < total && p <= 20);
      setUsers(all);
    } catch (err) {
      console.error("Failed to load users", err);
      setApiError("Failed to load users");
    }
  }, []);

  useEffect(() => { fetchAvailableUsers(); }, [fetchAvailableUsers]);

  const userOptions = useMemo(
    () =>
      users.map((u) => {
        const detail = u.contact_number || u.email || u.username || "";
        return {
          id: String(u.user_id),
          label: detail ? `${u.name || u.username}  —  ${detail}` : u.name || u.username,
          search: `${u.username || ""} ${u.email || ""} ${u.contact_number || ""}`,
        };
      }),
    [users]
  );

  const talukOptions = useMemo(
    () =>
      taluks.map((t) => ({
        id: String(t.TALUK_ID ?? t.id ?? t.taluk_id),
        label: t.TALUK_NAME || t.name || t.taluk_name || "Unknown Taluk",
      })),
    [taluks]
  );

  const villageOptions = useMemo(
    () =>
      villages.map((v) => {
        const name = v.VILLAGE_NAME || v.name || v.village_name || "Unknown Village";
        const suffix = [v.TALUK_NAME, v.DISTRICT_NAME].filter(Boolean).join(", ");
        return {
          id: String(v.VILLAGE_ID ?? v.id ?? v.village_id),
          label: suffix ? `${name}  —  ${suffix}` : name,
          talukId: String(v.TALUK_ID ?? v.taluk_id ?? v.talukId ?? ""),
        };
      }),
    [villages]
  );

  const areaOptions = useMemo(
    () =>
      areas
        .filter(
          (a) =>
            form.VILLAGE_ID &&
            String(a.VILLAGE_ID ?? a.village_id ?? a.villageId) === String(form.VILLAGE_ID)
        )
        .map((a) => ({
          id: String(a.AREA_ID ?? a.id ?? a.area_id),
          label: a.AREA_NAME || a.name || a.area_name || "Unknown Area",
        })),
    [areas, form.VILLAGE_ID]
  );

  // Update a field; if it already shows an error, re-check it while the user types
  const setField = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
    setErrors((p) => (p[key] ? { ...p, [key]: validateField(key, value) } : p));
  };

  const handleBlur = (key) =>
    setErrors((p) => ({ ...p, [key]: validateField(key, form[key]) }));

  const pickImage = (e, setFile, setRemove, errKey) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setFileErrors((p) => ({ ...p, [errKey]: "Please choose an image file (JPG, PNG or WEBP)." }));
      return;
    }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      setFileErrors((p) => ({ ...p, [errKey]: `Image must be ${MAX_IMAGE_MB} MB or smaller.` }));
      return;
    }
    setFileErrors((p) => ({ ...p, [errKey]: "" }));
    setFile(file);
    setRemove(false);
  };

  const onTalukChange = ({ selectedItem }) =>
    setForm((p) => ({ ...p, TALUK_ID: selectedItem?.id || "", VILLAGE_ID: "", AREA_ID: "" }));

  const onVillageChange = ({ selectedItem }) =>
    setForm((p) => ({
      ...p,
      VILLAGE_ID: selectedItem?.id || "",
      TALUK_ID: selectedItem?.talukId || p.TALUK_ID,
      AREA_ID: "",
    }));

  const onAreaChange = ({ selectedItem }) => setField("AREA_ID", selectedItem?.id || "");

  // ---- map helpers ----
  const moveTo = useCallback((coords) => {
    setMarkerPosition(coords);
    setMapCenter(coords);
    setForm((p) => ({
      ...p,
      LATITUDE: Number(coords.lat.toFixed(6)),
      LONGITUDE: Number(coords.lng.toFixed(6)),
    }));
  }, []);

  const reverseGeocode = useCallback((coords) => {
    if (!geocoderRef.current) return;
    geocoderRef.current.geocode({ location: coords }, (results, status) => {
      if (status === "OK" && results[0]) setSearchAddress(results[0].formatted_address || "");
    });
  }, []);

  const onMapClick = useCallback(
    (e) => {
      const coords = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      moveTo(coords);
      reverseGeocode(coords);
    },
    [moveTo, reverseGeocode]
  );

  const onAutocompleteLoad = useCallback((ac) => {
    autocompleteRef.current = ac;
    ac.setFields(["address_components", "geometry", "formatted_address", "name"]);
  }, []);

  const handlePlaceSelect = useCallback(() => {
    if (!autocompleteRef.current || !geocoderRef.current) return;
    const place = autocompleteRef.current.getPlace();
    if (!place?.geometry?.location) {
      setGmapError("No location found");
      return;
    }
    setGmapError("");
    const coords = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
    moveTo(coords);
    setSearchAddress(place.formatted_address || place.name || "");

    geocoderRef.current.geocode({ location: coords }, async (results, status) => {
      if (status !== "OK" || !results?.length) return;
      const geocoderResponse = {
        plus_code: results.find((r) => r.plus_code)?.plus_code || {},
        results: results.map((r) => ({
          ...r,
          geometry: {
            ...r.geometry,
            viewport: serializeBounds(r.geometry?.viewport),
            bounds: r.geometry?.bounds ? serializeBounds(r.geometry.bounds) : undefined,
          },
        })),
        status: status || "OK",
      };
      try {
        const response = await callApi("POST", "/master-extraction/extract-masters", geocoderResponse);
        const ex = response?.data?.data || response?.data || {};
        setForm((p) => ({
          ...p,
          TALUK_ID: ex.taluk_id ? String(ex.taluk_id) : p.TALUK_ID,
          VILLAGE_ID: ex.village_id ? String(ex.village_id) : p.VILLAGE_ID,
          AREA_ID: ex.area_id ? String(ex.area_id) : p.AREA_ID,
        }));
      } catch (err) {
        console.error("Master extraction failed", err);
      }
    });
  }, [moveTo]);

  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setGmapError("Geolocation not supported");
      return;
    }
    setLocating(true);
    setGmapError("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        moveTo(coords);
        reverseGeocode(coords);
        setLocating(false);
      },
      () => {
        setGmapError("Unable to retrieve location");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, [moveTo, reverseGeocode]);

  // ---- list ----
  const fetchBuilders = useCallback(async () => {
    setLoading(true);
    setApiError("");
    try {
      const params = {};
      if (appliedFilters.verification_status) params.verification_status = appliedFilters.verification_status;
      if (appliedFilters.status) params.status = appliedFilters.status;
      if (appliedFilters.start_date) params.start_date = appliedFilters.start_date;
      if (appliedFilters.end_date) params.end_date = appliedFilters.end_date;

      const res = await callApi("GET", "/builder-profiles/", params);
      const d = res?.data;
      const list = Array.isArray(d) ? d : d?.items || d?.data || [];
      if (mountedRef.current) setRows(list);
    } catch (err) {
      console.error("fetchBuilders error:", err);
      if (mountedRef.current) setApiError(err?.message || "Unable to load builders.");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    mountedRef.current = true;
    fetchBuilders();
    return () => { mountedRef.current = false; };
  }, [fetchBuilders]);

  const filteredRows = useMemo(() => {
    const s = searchTerm.trim().toLowerCase();
    return rows.filter((r) => {
      if (!s) return true;
      return [r.BUSINESS_NAME, r.OFFICE_CONTACT_NUMBER, r.RERA_NUMBER,
              r.GST_NUMBER, r.PAN_NUMBER, r.USER_ID, r.VERIFICATION_STATUS]
        .map((v) => String(v ?? "")).join(" ").toLowerCase().includes(s);
    });
  }, [rows, searchTerm]);

  const tableRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize).map((r) => ({
      id: String(r.BUILDER_PROFILE_ID),
      businessName: r.BUSINESS_NAME || "N/P",
      contact: r.OFFICE_CONTACT_NUMBER || "N/P",
      rera: r.RERA_NUMBER || "N/P",
      gst: r.GST_NUMBER || "N/P",
      verificationStatus: r.VERIFICATION_STATUS || "PENDING",
      status: r.STATUS || "N/P",
      createdAt: r.CREATED_TIMESTAMP || "N/P",
      actions: "",
    }));
  }, [filteredRows, page, pageSize]);

  const findRow = (id) => rows.find((r) => String(r.BUILDER_PROFILE_ID) === String(id));

  // ---- add / edit ----
  const resetMap = () => {
    setMarkerPosition(null);
    setMapCenter(defaultCenter);
    setSearchAddress("");
    setGmapError("");
  };

  const openAdd = () => {
    setEditing(null); setForm(EMPTY); setLogo(null); setBanner(null);
    setExistingLogo(""); setExistingBanner(""); setRemoveLogo(false); setRemoveBanner(false);
    setDocFile(null); setExistingDoc(""); setRemoveDoc(false);
    setErrors({}); setFileErrors(NO_FILE_ERRORS);
    fetchAvailableUsers();
    resetMap(); setApiError(""); setIsFormOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    const f = { ...EMPTY };
    Object.keys(EMPTY).forEach((k) => { f[k] = row[k] ?? ""; });
    ["TALUK_ID", "VILLAGE_ID", "AREA_ID"].forEach((k) => { f[k] = row[k] ? String(row[k]) : ""; });
    setForm(f); setLogo(null); setBanner(null);
    setExistingLogo(row.LOGO_URL || ""); setExistingBanner(row.BANNER_IMAGE_URL || "");
    setDocFile(null); setRemoveDoc(false); setExistingDoc(row.REGISTRATION_DOCUMENT_URL || "");
    setRemoveLogo(false); setRemoveBanner(false);
    setErrors({}); setFileErrors(NO_FILE_ERRORS);
    resetMap();
    const lat = parseFloat(row.LATITUDE);
    const lng = parseFloat(row.LONGITUDE);
    if (lat && lng) {
      setMarkerPosition({ lat, lng });
      setMapCenter({ lat, lng });
    }
    setApiError(""); setIsFormOpen(true);
  };

  const removeExistingFile = async (fileKey) => {
    if (!editing) return;
    setRemovingFile(fileKey);
    setFileErrors((p) => ({ ...p, [fileKey]: "" }));
    try {
      await callApi(
        "DELETE",
        `/builder-profiles/${editing.BUILDER_PROFILE_ID}/files`,
        null,
        { params: { file_type: FILE_TYPE_BY_KEY[fileKey] } }
      );
      if (fileKey === "logo")   { setExistingLogo("");   setLogo(null);   }
      if (fileKey === "banner") { setExistingBanner(""); setBanner(null); }
      if (fileKey === "doc")    { setExistingDoc("");    setDocFile(null); }
      setSuccessMsg("File removed.");
    } catch (err) {
      console.error("Delete file error:", err);
      setFileErrors((p) => ({
        ...p,
        [fileKey]: err?.message || "Failed to remove file.",
      }));
    } finally {
      setRemovingFile("");
    }
  };

  const handleSave = async () => {
    const found = validateAll(form);
    setErrors(found);
    const firstKey = Object.keys(found)[0];
    if (firstKey) {
      setApiError("");
      scrollToField(firstKey);
      return;
    }

    setSaving(true);
    setApiError("");
    try {
      const fd = new FormData();
      Object.entries({ ...form, ...FIXED_LOCATION }).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) fd.append(k, v);
      });
      if (logo) fd.append("LOGO_FILE", logo);
      if (banner) fd.append("BANNER_FILE", banner);

      if (docFile) fd.append("REGISTRATION_DOCUMENT_URL", docFile);

      if (editing) {
        fd.append("UPDATED_BY_USER_ID", 1);
        await callApi("PUT", `/builder-profiles/${editing.BUILDER_PROFILE_ID}`, fd);
        setSuccessMsg("Builder updated successfully.");
      } else {
        fd.append("CREATED_BY_USER_ID", 1);
        await callApi("POST", "/builder-profiles/", fd);
        setSuccessMsg("Builder created successfully.");
      }
      setIsFormOpen(false);
      await fetchBuilders();
    } catch (err) {
      console.error("Save builder error:", err);
      const detail = err?.data?.detail;
      if (Array.isArray(detail)) {
        // Put each backend error under its own field; the rest go in the banner
        const fieldErrs = {};
        const other = [];
        detail.forEach((d) => {
          const key = d.loc?.[d.loc.length - 1];
          const msg = String(d.msg || "Invalid value").replace(/^Value error,\s*/i, "");
          if (key && Object.prototype.hasOwnProperty.call(EMPTY, key)) {
            fieldErrs[key] = msg;
          } else {
            other.push(`${key}: ${msg}`);
          }
        });
        setErrors((p) => ({ ...p, ...fieldErrs }));
        setApiError(other.join(" | "));
        const firstServerKey = Object.keys(fieldErrs)[0];
        if (firstServerKey) scrollToField(firstServerKey);
      } else {
        setApiError(err?.message || "Failed to save builder.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleStatusConfirmed = async () => {
    if (!confirm) return;
    setUpdatingStatus(true);
    setApiError("");
    try {
      await callApi("POST", `/builder-profiles/${confirm.row.BUILDER_PROFILE_ID}/verify`, {
        builder_profile_id: confirm.row.BUILDER_PROFILE_ID,
        verification_status: confirm.status,
      });
      setSuccessMsg(`Builder marked as ${confirm.status.toLowerCase()}.`);
      setConfirm(null);
      await fetchBuilders();
    } catch (err) {
      console.error("Verify builder error:", err);
      setApiError(err?.message || "Failed to update verification status.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setApiError("");
    try {
      await callApi("DELETE", `/builder-profiles/${deleteTarget.BUILDER_PROFILE_ID}`);
      setSuccessMsg("Builder deleted successfully.");
      setDeleteTarget(null);
      await fetchBuilders();
    } catch (err) {
      console.error("Delete builder error:", err);
      setDeleteTarget(null);
      setApiError(err?.message || "Failed to delete builder.");
    } finally {
      setDeleting(false);
    }
  };

  const renderActions = (id) => {
    const row = findRow(id);
    if (!row) return null;
    return (
      <OverflowMenu light flipped inlineIconDescription="More actions">
        <OverflowMenuItem itemText="Edit Builder" onClick={() => openEdit(row)} />
        <OverflowMenuItem itemText="Verify" onClick={() => setConfirm({ row, status: VERIFY.VERIFIED })} />
        <OverflowMenuItem itemText="Reject" onClick={() => setConfirm({ row, status: VERIFY.REJECTED })} />
        <OverflowMenuItem itemText="Delete" isDelete hasDivider onClick={() => setDeleteTarget(row)} />
      </OverflowMenu>
    );
  };

  const renderCell = (cell) => {
    if (cell.info.header === "actions") return renderActions(cell.id.split(":")[0]);
    if (cell.info.header === "verificationStatus") {
      const k = norm(cell.value);
      const type = k === "VERIFIED" ? "green" : k === "REJECTED" ? "gray" : "red";
      return <Tag size="sm" type={type}>{cell.value}</Tag>;
    }
    return cell.value;
  };

  const errorTextStyle = { color: "#da1e28", fontSize: 12, margin: "4px 0 8px" };

  const filterVillage = ({ item, inputValue }) =>
    !inputValue || item.label.toLowerCase().includes(inputValue.toLowerCase());

  return (
    <>
      {/* Google autocomplete dropdown must sit above the Carbon modal */}
      <style>{`.pac-container { z-index: 99999 !important; }`}</style>

      {successMsg && (
        <InlineNotification kind="success" title="Success" subtitle={successMsg}
          onCloseButtonClick={() => setSuccessMsg("")} style={{ marginBottom: "1rem" }} />
      )}
      {apiError && !isFormOpen && (
        <InlineNotification kind="error" title="Error" subtitle={apiError}
          onCloseButtonClick={() => setApiError("")} style={{ marginBottom: "1rem" }} />
      )}

      {/* --- NEW FILTER CARD --- */}
      <div style={{
        backgroundColor: "#f4f4f4",
        padding: "1.5rem",
        borderRadius: "8px",
        marginBottom: "2rem",
        border: "1px solid #e0e0e0"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "1.5rem" }}>
          <Filter size={20} />
          <h4 style={{ margin: 0, fontWeight: 400, fontSize: "1.25rem" }}>Filter Builder Report</h4>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "1.5rem", marginBottom: "1.5rem" }}>
          <Dropdown
            id="filter-verification"
            titleText="Verification Status"
            label="Choose"
            items={VERIFICATION_OPTIONS}
            itemToString={(i) => i?.label || ""}
            selectedItem={VERIFICATION_OPTIONS.find((o) => o.id === draftFilters.verification_status) || VERIFICATION_OPTIONS[0]}
            onChange={({ selectedItem }) => {
              setDraftFilters((p) => ({ ...p, verification_status: selectedItem?.id || "" }));
            }}
          />

          <Dropdown
            id="filter-status"
            titleText="Status"
            label="Choose"
            items={STATUS_OPTIONS}
            itemToString={(i) => i?.label || ""}
            selectedItem={STATUS_OPTIONS.find((o) => o.id === draftFilters.status) || STATUS_OPTIONS[0]}
            onChange={({ selectedItem }) => {
              setDraftFilters((p) => ({ ...p, status: selectedItem?.id || "" }));
            }}
          />

          <TextInput
            id="filter-start-date"
            labelText="Start Date"
            type="date"
            placeholder="dd/mm/yyyy"
            value={draftFilters.start_date}
            max={draftFilters.end_date || undefined}
            onChange={(e) => {
              setDraftFilters((p) => ({ ...p, start_date: e.target.value }));
            }}
          />

          <TextInput
            id="filter-end-date"
            labelText="End Date"
            type="date"
            placeholder="dd/mm/yyyy"
            value={draftFilters.end_date}
            min={draftFilters.start_date || undefined}
            onChange={(e) => {
              setDraftFilters((p) => ({ ...p, end_date: e.target.value }));
            }}
          />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem" }}>
          <Button
            kind="secondary"
            style={{ backgroundColor: "#262626", color: "white" }}
            onClick={() => {
              const empty = { verification_status: "", status: "", start_date: "", end_date: "" };
              setDraftFilters(empty);
              setAppliedFilters(empty);
              setPage(1);
            }}
          >
            Reset
          </Button>
          <Button
            kind="primary"
            onClick={() => {
              setAppliedFilters(draftFilters);
              setPage(1);
            }}
          >
            Search
          </Button>
        </div>
      </div>

      <TableContainer title="Builder List">
        <TableToolbar>
          <TableToolbarContent>
            <TableToolbarSearch 
              persistent 
              id="builder-search" 
              placeHolderText="Filter table (client-side)" 
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }} 
            />
            <Button renderIcon={Add} onClick={openAdd}>Add Builder</Button>
          </TableToolbarContent>
        </TableToolbar>

        {loading ? (
          <InlineLoading description="Loading builders..." style={{ padding: 16 }} />
        ) : (
          <DataTable rows={tableRows} headers={headers}>
            {({ rows: dtRows, headers: dtHeaders, getTableProps, getHeaderProps, getRowProps }) => (
              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    {dtHeaders.map((h) => (
                      <TableHeader key={h.key} {...getHeaderProps({ header: h })}>{h.header}</TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dtRows.length === 0 && (
                    <TableRow><TableCell colSpan={headers.length}>No builders found</TableCell></TableRow>
                  )}
                  {dtRows.map((row) => (
                    <TableRow key={row.id} {...getRowProps({ row })}>
                      {row.cells.map((cell) => (
                        <TableCell key={cell.id}>{renderCell(cell)}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </DataTable>
        )}

        <Pagination page={page} pageSize={pageSize} pageSizes={[10, 20, 50]}
          totalItems={filteredRows.length}
          onChange={({ page: p, pageSize: ps }) => { setPage(p); setPageSize(ps); }} />

        {/* Add / Edit modal */}
        <Modal open={isFormOpen} size="lg"
          modalHeading={editing ? "Edit Builder" : "Add Builder"}
          primaryButtonText={saving ? "Saving..." : "Save"}
          secondaryButtonText="Cancel"
          primaryButtonDisabled={saving}
          onRequestClose={() => !saving && setIsFormOpen(false)}
          onRequestSubmit={handleSave}>
          {apiError && (
            <InlineNotification kind="error" lowContrast title="Error" subtitle={apiError}
              onCloseButtonClick={() => setApiError("")} style={{ marginBottom: "1rem" }} />
          )}

          <div style={{ marginBottom: 16 }}>
            <ComboBox id="builder-user" titleText="Select User *"
              placeholder="Search by name, phone or email..."
              helperText={editing ? "The user cannot be changed after the profile is created." : ""}
              disabled={!!editing}
              items={userOptions}
              itemToString={(i) => i?.label || ""}
              shouldFilterItem={({ item, inputValue }) =>
                !inputValue ||
                `${item.label} ${item.search}`.toLowerCase().includes(inputValue.toLowerCase())}
              selectedItem={
                findOpt(userOptions, form.USER_ID) ||
                (form.USER_ID ? { id: String(form.USER_ID), label: `User ID ${form.USER_ID}`, search: "" } : null)
              }
              invalid={!!errors.USER_ID}
              invalidText={errors.USER_ID}
              onChange={({ selectedItem }) => {
                const id = selectedItem?.id || "";
                setForm((p) => ({ ...p, USER_ID: id }));
                setErrors((p) => ({ ...p, USER_ID: validateField("USER_ID", id) }));
              }} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
{FIELDS.map(([key, label]) => (
  <TextInput 
    key={key} 
    id={`builder-${key}`} 
    labelText={label}
    placeholder={PLACEHOLDERS[key] || ""}
    type={NUMERIC_KEYS.includes(key) ? "number" : "text"}
    value={form[key]}
    onChange={(e) =>
      setField(
        key,
        ["GST_NUMBER", "PAN_NUMBER"].includes(key)
          ? e.target.value.toUpperCase()
          : e.target.value
      )
    }
    // ✅ ADD THESE THREE LINES:
    invalid={!!errors[key]}
    invalidText={errors[key]}
    onBlur={() => handleBlur(key)}
  />
))}
          </div>

          <div style={{ marginTop: 16 }}>
            <TextArea id="builder-about" labelText="About" rows={3} value={form.ABOUT}
              onChange={(e) => setField("ABOUT", e.target.value)} />
          </div>

          {/* ---------- Office location ---------- */}
          {isFormOpen && (
            <>
              <h5 style={{ margin: "24px 0 8px", color: "#525252" }}>Office Location</h5>

              {(gmapError || loadError) && (
                <InlineNotification kind="error" lowContrast title="Map"
                  subtitle={gmapError || "Failed to load Google Maps (check REACT_APP_GOOGLE_MAPS_API_KEY)"}
                  hideCloseButton style={{ marginBottom: "1rem" }} />
              )}

              {mapsLoaded ? (
                <>
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                      <Autocomplete onLoad={onAutocompleteLoad} onPlaceChanged={handlePlaceSelect}>
                        <TextInput id="builder-map-search" labelText="Search Location"
                          placeholder="Search address..." value={searchAddress}
                          onChange={(e) => setSearchAddress(e.target.value)} />
                      </Autocomplete>
                    </div>
                    <Button kind="secondary" onClick={getCurrentLocation} disabled={locating}>
                      {locating ? "Getting Location..." : "Current Location"}
                    </Button>
                  </div>

                  <GoogleMap mapContainerStyle={mapContainerStyle} center={mapCenter} zoom={14}
                    onClick={onMapClick}
                    options={{
                      streetViewControl: false, mapTypeControl: false,
                      fullscreenControl: false, zoomControl: true, clickableIcons: false,
                    }}>
                    {markerPosition && (
                      <Marker position={markerPosition} draggable
                        onDragEnd={(e) => {
                          const coords = { lat: e.latLng.lat(), lng: e.latLng.lng() };
                          moveTo(coords);
                          reverseGeocode(coords);
                        }} />
                    )}
                  </GoogleMap>
                </>
              ) : (
                !loadError && <InlineLoading description="Loading map..." />
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
                <TextInput id="builder-lat" labelText="Latitude" value={form.LATITUDE} readOnly />
                <TextInput id="builder-lng" labelText="Longitude" value={form.LONGITUDE} readOnly />

                <TextInput id="builder-district" labelText="District" value="Kanyakumari" readOnly />
                <div />

                <div style={{ gridColumn: "1 / span 2" }}>
                  <ComboBox id="builder-village" titleText="Search Village"
                    placeholder="Type to search any village..."
                    items={villageOptions}
                    itemToString={(i) => i?.label || ""}
                    shouldFilterItem={filterVillage}
                    selectedItem={findOpt(villageOptions, form.VILLAGE_ID)}
                    onChange={onVillageChange} />
                </div>

                <Dropdown id="builder-taluk" titleText="Taluk" label="Select"
                  items={talukOptions}
                  itemToString={(i) => i?.label || ""}
                  selectedItem={findOpt(talukOptions, form.TALUK_ID)}
                  onChange={onTalukChange} />

                <Dropdown id="builder-area" titleText="Area" label="Select"
                  items={areaOptions}
                  itemToString={(i) => i?.label || ""}
                  selectedItem={findOpt(areaOptions, form.AREA_ID)}
                  onChange={onAreaChange} />

                <TextInput id="builder-addr1" labelText="Address Line 1"
                  value={form.ADDRESS_LINE_1}
                  onChange={(e) => setField("ADDRESS_LINE_1", e.target.value)} />
                <TextInput id="builder-addr2" labelText="Address Line 2"
  value={form.ADDRESS_LINE_2}
  placeholder="Area, landmark" 
  onChange={(e) => setField("ADDRESS_LINE_2", e.target.value)} />
                <TextInput id="builder-landmark" labelText="Landmark"
                  value={form.LANDMARK}
                  onChange={(e) => setField("LANDMARK", e.target.value)} />
                <TextInput id="builder-pin" labelText="Pincode" 
                  placeholder="6-digit pincode"
                  inputMode="numeric" maxLength={6} value={form.PIN_CODE}
                  invalid={!!errors.PIN_CODE} invalidText={errors.PIN_CODE}
                  onBlur={() => handleBlur("PIN_CODE")}
                  onChange={(e) => setField("PIN_CODE", e.target.value.replace(/\D/g, "").slice(0, 6))} />
              </div>
            </>
          )}

          <FormGroup legendText="Registration Document (PDF)" style={{ marginTop: 16 }}>
            <input ref={docInputRef} type="file" accept="application/pdf,.pdf" style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files[0];
                e.target.value = "";
                if (!file) return;
                if (file.type !== "application/pdf") { setFileErrors((p) => ({ ...p, doc: "Only PDF files are allowed." })); return; }
                if (file.size > MAX_PDF_MB * 1024 * 1024) { setFileErrors((p) => ({ ...p, doc: `PDF must be ${MAX_PDF_MB} MB or smaller.` })); return; }
                setFileErrors((p) => ({ ...p, doc: "" })); setDocFile(file); setRemoveDoc(false);
              }} />
            {(docFile || (existingDoc && !removeDoc)) && (
              <p style={{ marginBottom: 8 }}>
                {docFile ? docFile.name : existingDoc.split("/").pop()}{" "}
                <a href={docPreview || existingDoc} target="_blank" rel="noreferrer">View</a>
              </p>
            )}
            {fileErrors.doc && <p style={errorTextStyle}>{fileErrors.doc}</p>}
            <Button size="sm" kind="tertiary" onClick={() => docInputRef.current?.click()}>
              {docFile || (existingDoc && !removeDoc) ? "Change" : "Choose PDF"}
            </Button>
            {(docFile || (existingDoc && !removeDoc)) && (
              <Button size="sm" kind="danger--ghost"
                disabled={removingFile === "doc"}
onClick={() => {
  if (docFile) {
    setDocFile(null);            // locally-picked file → just clear
  } else if (existingDoc) {
    removeExistingFile("doc");   // server file → DELETE call
  }
}}>
                Remove
              </Button>
            )}
          </FormGroup>

          <FormGroup legendText="Images" style={{ marginTop: 16 }}>
            <div style={{ display: "flex", gap: 32, alignItems: "flex-start", flexWrap: "wrap" }}>
              {/* Logo */}
              <div>
                <p className="cds--label">Logo</p>
                {(logoPreview || (existingLogo && !removeLogo)) && (
                  <img src={logoPreview || existingLogo} alt="Logo preview"
                    style={{ width: 120, height: 120, objectFit: "contain", border: "1px solid #e0e0e0",
                      borderRadius: 8, display: "block", marginBottom: 8 }} />
                )}
                <input ref={logoInputRef} type="file" accept="image/*" style={{ display: "none" }}
                  onChange={(e) => pickImage(e, setLogo, setRemoveLogo, "logo")} />
                {fileErrors.logo && <p style={errorTextStyle}>{fileErrors.logo}</p>}
                <Button size="sm" kind="tertiary" onClick={() => logoInputRef.current?.click()}>
                  {logoPreview || (existingLogo && !removeLogo) ? "Change" : "Choose Logo"}
                </Button>
                {(logoPreview || (existingLogo && !removeLogo)) && (
                  <Button size="sm" kind="danger--ghost"
                    disabled={removingFile === "logo"}
onClick={() => {
  if (logo) {
    setLogo(null);
  } else if (existingLogo) {
    removeExistingFile("logo");
  }
}}>
                    Remove
                  </Button>
                )}
              </div>

              {/* Banner */}
              <div>
                <p className="cds--label">Banner</p>
                {(bannerPreview || (existingBanner && !removeBanner)) && (
                  <img src={bannerPreview || existingBanner} alt="Banner preview"
                    style={{ width: 320, height: 120, objectFit: "cover", border: "1px solid #e0e0e0",
                      borderRadius: 8, display: "block", marginBottom: 8 }} />
                )}
                <input ref={bannerInputRef} type="file" accept="image/*" style={{ display: "none" }}
                  onChange={(e) => pickImage(e, setBanner, setRemoveBanner, "banner")} />
                {fileErrors.banner && <p style={errorTextStyle}>{fileErrors.banner}</p>}
                <Button size="sm" kind="tertiary" onClick={() => bannerInputRef.current?.click()}>
                  {bannerPreview || (existingBanner && !removeBanner) ? "Change" : "Choose Banner"}
                </Button>
                {(bannerPreview || (existingBanner && !removeBanner)) && (
                  <Button size="sm" kind="danger--ghost"
                    disabled={removingFile === "banner"}
onClick={() => {
  if (banner) {
    setBanner(null);
  } else if (existingBanner) {
    removeExistingFile("banner");
  }
}}>
                    Remove
                  </Button>
                )}
              </div>
            </div>
          </FormGroup>
        </Modal>

        {/* Verify / Reject confirm */}
        <Modal open={!!confirm} danger={confirm?.status === VERIFY.REJECTED}
          modalHeading={confirm?.status === VERIFY.REJECTED ? "Reject Builder" : "Verify Builder"}
          primaryButtonText={updatingStatus ? "" : "Confirm"}
          secondaryButtonText="Cancel"
          primaryButtonDisabled={updatingStatus}
          onRequestClose={() => !updatingStatus && setConfirm(null)}
          onRequestSubmit={handleStatusConfirmed}>
          <p>
            Mark <strong>{confirm?.row?.BUSINESS_NAME}</strong> as{" "}
            <strong>{confirm?.status?.toLowerCase()}</strong>?
          </p>
          {updatingStatus && <InlineLoading description="Updating..." style={{ marginTop: 12 }} />}
        </Modal>

        {/* Delete confirm */}
        <Modal open={!!deleteTarget} danger
          modalHeading="Delete Builder"
          primaryButtonText={deleting ? "" : "Delete"}
          secondaryButtonText="Cancel"
          primaryButtonDisabled={deleting}
          onRequestClose={() => !deleting && setDeleteTarget(null)}
          onRequestSubmit={handleDeleteConfirmed}>
          <p>
            Are you sure you want to delete <strong>{deleteTarget?.BUSINESS_NAME}</strong>?
            This action cannot be undone.
          </p>
          {deleting && <InlineLoading description="Deleting..." style={{ marginTop: 12 }} />}
        </Modal>
      </TableContainer>
    </>
  );
}